import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isVerified } from "@/lib/verification";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { VellutoAddress, stripTag } from "@/components/VellutoAddress";
import { Lock, MapPin, Radio, ShieldCheck, Sparkles, Flame } from "lucide-react";

export default function CreatorLink() {
  const params = useParams();
  const handle = stripTag(params.tag || params.handle || "");
  const { user } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(undefined);

  useEffect(() => {
    api.get(`/c/${handle}`).then(({ data }) => setC(data)).catch(() => setC(null));
  }, [handle]);

  useEffect(() => {
    if (c && user && isVerified(user)) nav(`/profile/${c.id}`, { replace: true });
  }, [c, user, nav]);

  const go = (path) => {
    if (c) { sessionStorage.setItem("velluto_next", `/profile/${c.id}`); localStorage.setItem("velluto_ref", c.handle); }
    nav(path);
  };

  if (c === undefined || user === null) return <div className="pt-32 text-center text-text-muted">Opening the door…</div>;
  if (c === null) {
    return (
      <div data-testid="creator-link-missing" className="pt-32 pb-24 max-w-xl mx-auto px-6 text-center">
        <h1 className="font-display text-5xl font-light">This link isn't active</h1>
        <p className="text-text-muted mt-4">The creator may have changed their handle. Discover more of Velluto instead.</p>
        <Link to="/" className="inline-block mt-8 px-8 py-4 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.2em]">Enter Velluto</Link>
      </div>
    );
  }

  const unverified = user && !isVerified(user);

  return (
    <div data-testid="creator-link-page" className="pt-24 sm:pt-28 pb-24 max-w-5xl mx-auto px-6">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
        className="grid lg:grid-cols-[420px_1fr] gap-8 lg:gap-14 items-center">
        <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden border border-gold/30 bg-surface">
          {c.preview_url
            ? <img data-testid="creator-link-preview" src={c.preview_url} alt="" className="w-full h-full object-cover scale-110 blur-md" draggable={false} />
            : <div className="w-full h-full bg-gradient-to-br from-wine/40 to-ink" />}
          <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-ink/10" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
            <span className="w-16 h-16 rounded-full bg-ink/70 border border-gold/40 backdrop-blur-md flex items-center justify-center"><Lock size={22} className="text-gold" /></span>
            <p className="mt-4 text-xs uppercase tracking-[0.25em] text-gold">Members only</p>
            <p className="text-sm text-text-muted mt-1">Photos unlock once you're verified 18+</p>
          </div>
          {c.streaming_live && <span className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600 text-[10px] uppercase tracking-[0.15em]"><Radio size={11} className="animate-pulse" /> Live now</span>}
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold flex items-center gap-2"><Sparkles size={13} /> Velluto Society · {c.is_creator ? "Creator" : "Member"}</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light mt-4 leading-[0.95] flex flex-wrap items-center gap-x-4 gap-y-2">
            <span data-testid="creator-link-name">{c.display_name}</span>{c.age && <span className="text-text-muted">{c.age}</span>}
            <VerifiedBadge user={c} size="lg" testId="creator-link-verified" />
          </h1>
          <div className="flex flex-wrap gap-4 mt-5 text-sm text-text-muted">
            <VellutoAddress user={c} testId="creator-link-address" className="!text-sm" />
            {c.location && <span className="flex items-center gap-1.5"><MapPin size={14} /> {c.location}</span>}
            {c.kinks_count > 0 && <span className="flex items-center gap-1.5"><Flame size={14} className="text-gold" /> {c.kinks_count} interests listed</span>}
          </div>
          {c.bio_snippet && <p className="text-text-main/80 mt-6 max-w-lg text-base">{c.bio_snippet}</p>}

          <div data-testid="creator-link-gate" className="mt-9 rounded-3xl border border-gold/30 bg-surface p-6 sm:p-7">
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold"><ShieldCheck size={14} /> {unverified ? "Verify to see more" : "Sign up to see more"}</p>
            <p className="text-sm text-text-muted mt-2">
              {unverified
                ? `Finish your 18+ identity check to see ${c.display_name}'s photos, message them and join their live room.`
                : `Velluto is a private, verified 18+ society. Create a free account and verify your ID to see ${c.display_name}'s full profile, photos and live room.`}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              {unverified ? (
                <button data-testid="creator-link-verify" onClick={() => nav("/verify", { state: { from: `/profile/${c.id}` } })}
                  className="flex-1 py-4 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.2em] hover:bg-gold/90 transition-colors">Verify I'm 18+</button>
              ) : (
                <>
                  <button data-testid="creator-link-signup" onClick={() => go("/auth?mode=register")}
                    className="flex-1 py-4 rounded-full bg-gold text-ink text-xs uppercase tracking-[0.2em] hover:bg-gold/90 transition-colors">Sign up free</button>
                  <button data-testid="creator-link-login" onClick={() => go("/auth")}
                    className="flex-1 py-4 rounded-full border border-white/15 text-xs uppercase tracking-[0.2em] text-text-muted hover:text-text-main transition-colors">I have an account</button>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
